import React from 'react';
import { SupersetFrame } from '@growthaxl/web-foundation';
import { config } from '../../../../config';
import type { ChartEmbedBlock } from '../../types';

/**
 * feat-70, DR-15 (defense-in-depth): re-validates a `chart_embed` block's URL
 * client-side, even though DESO already validated it server-side against
 * `DESO_EMBED_ALLOWED_HOSTS` (Step 8). Guards against config drift/build
 * mismatch silently rendering an untrusted iframe — only `https:` URLs whose
 * hostname is in `allowedHosts` (case-insensitive) are trusted.
 */
export function isEmbedUrlTrusted(url: string, allowedHosts: readonly string[]): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }
  if (parsed.protocol !== 'https:') return false;
  const host = parsed.hostname.toLowerCase();
  return allowedHosts.some(allowed => allowed.toLowerCase() === host);
}

export interface ChartEmbedBlockViewProps {
  block: ChartEmbedBlock;
}

export function ChartEmbedBlockView({ block }: ChartEmbedBlockViewProps): React.ReactElement {
  const trusted = isEmbedUrlTrusted(block.embed_view_url, config.embedAllowedHosts);

  if (!trusted) {
    return (
      <div
        data-testid="block-chart_embed-untrusted"
        className="border border-border-default rounded-8 px-3 py-2.5 text-text-secondary text-[13px] italic"
      >
        This chart could not be displayed because its source is not trusted.
      </div>
    );
  }

  return (
    <div data-testid="block-chart_embed" className="rounded-8 overflow-hidden border border-border-default">
      <SupersetFrame
        config={{ src: block.embed_view_url, dashboard: block.title, chart_type: block.chart_type }}
      />
    </div>
  );
}
